import styled from 'styled-components';
import { Button } from '../Button';
import { HiPlus } from 'react-icons/hi';
import { MdLockOutline, MdEditNote } from 'react-icons/md';
import { RiShareForwardLine } from 'react-icons/ri';
import {
  createNewEntity,
  deleteEntity,
  duplicateEntity,
  renameEntity,
  selectActiveAccount,
  selectUser,
  submitCurrentEntity,
} from '../../reducers/account.reducer';
import { useDispatch, useSelector } from 'react-redux';
import { Chips } from '../chips/Chips';
import { Pie } from '../pie/Pie';
import { COLORS, MODAL_TYPES } from '../../utils/constants';

import {
  getEntityConformityPercentage,
  getAuthorizedSurveys,
  filterSharedEntities,
  isEntityConformityOK,
  totalSurveyCount,
  getEntityTotalFillingPercentage,
  addEventTracking,
} from '../../utils/utils';
import { MoreOptions } from '../more-option/MoreOption';
import { Modal } from '../modal/Modal';
import { InputComponent } from '../form/InputComponent';
import { setCurrentEntity, setCurrentSurvey, setShareModal, setModal } from '../../reducers/app.reducer';
import { useEffect, useRef, useState } from 'react';
import { paths } from '@routes/routes.constants';
import { useHistory } from 'react-router-dom';

export interface EntityTableProps {}

export const EntityTable = ({}: EntityTableProps) => {
  const [displayNewEntityModal, setDisplayNewEntityModal] = useState(false);
  const [newEntityName, setNewName] = useState(null);
  const [displayRenameEntityModal, setDisplayRenameEntityModal] = useState({
    show: false,
    entity: null,
  });
  const [displayDuplicateEntityModal, setDisplayDuplicateEntityModal] = useState({
    show: false,
    entity: null,
  });
  const [displayDeleteEntityModal, setDisplayDeleteEntityModal] = useState({
    show: false,
    entity: null,
  });

  //REDUX
  const dispatch = useDispatch<any>();
  const history = useHistory();
  const activeAccount = useSelector(selectActiveAccount);
  const currentUser = useSelector(selectUser);

  const timerRef = useRef(null);

  const handleShowNewEntityModal = () => {
    setNewName(null);
    setDisplayNewEntityModal(true);
  };

  const handleShowRenameEntityModal = entity => {
    setDisplayRenameEntityModal({
      show: true,
      entity: entity,
    });
  };

  const handleShowDuplicateEntityModal = entity => {
    setDisplayDuplicateEntityModal({
      show: true,
      entity: entity,
    });
  };

  const handleShowDeleteEntityModal = entity => {
    setDisplayDeleteEntityModal({
      show: true,
      entity: entity,
    });
  };

  const closeDeleteEntityModal = () => {
    decomposedClose(setDisplayDeleteEntityModal, displayDeleteEntityModal);
  };

  const closeRenameEntityModal = () => {
    setNewName(null);
    decomposedClose(setDisplayRenameEntityModal, displayRenameEntityModal);
  };

  const closeDuplicateEntityModal = () => {
    setNewName(null);
    decomposedClose(setDisplayDuplicateEntityModal, displayDuplicateEntityModal);
  };

  const decomposedClose = (setter, values) => {
    setNewName(null);
    setter({
      ...values,
      show: false,
    });
    const timer = setTimeout(() => {
      setter({
        show: false,
        entity: null,
      });
    }, 500);
    timerRef.current = timer;
  };

  const handleAddEntity = async () => {
    addEventTracking('Entity', 'Add new entity', 'Via button');
    dispatch(createNewEntity({ accountId: activeAccount?._id, userId: currentUser._id, name: newEntityName?.trim() }));
    setDisplayNewEntityModal(false);
  };

  const handleCloneEntity = async () => {
    addEventTracking('Entity', 'Add new entity', 'Via duplicate');
    dispatch(
      duplicateEntity({ accountId: activeAccount?._id, entityId: displayDuplicateEntityModal?.entity?._id, entityName: newEntityName?.trim() })
    );
    closeDuplicateEntityModal();
  };

  const handleDeleteEntity = async (entity: any) => {
    addEventTracking('Entity', 'Delete entity');
    dispatch(deleteEntity({ accountId: activeAccount?._id, entityId: entity?._id }));
    closeDeleteEntityModal();
  };

  const handleEntityChangeName = async () => {
    addEventTracking('Entity', 'Rename entity');
    dispatch(renameEntity({ accountId: activeAccount?._id, entityId: displayRenameEntityModal.entity._id, entityName: newEntityName?.trim() }));
    closeRenameEntityModal();
  };

  const getEntityOptions = entity => [
    ...(entity?.submission?.submitted !== true && currentUser.role !== 'guest'
      ? [
          {
            label: "Partager l'entité",
            onClick: () => shareItem(entity),
            icon: RiShareForwardLine,
          },
          {
            label: "Dupliquer l'entité",
            onClick: () => handleShowDuplicateEntityModal(entity),
          },
          {
            label: "Renommer l'entité",
            onClick: () => handleShowRenameEntityModal(entity),
          },
          {
            label: "Soumettre l'entité",
            onClick: () => handleSubmitEntity(activeAccount._id, entity),
          },
          {
            label: "Supprimer l'entité",
            onClick: () => handleShowDeleteEntityModal(entity),
            color: '#ed3f3e',
          },
        ]
      : []),
  ];

  const handleSubmitEntity = async (accountId: string, entity: any) => {
    if (isEntityConformityOK(entity)) {
      addEventTracking('Entity', 'Submit entity', 'Via dashboard group');
      dispatch(submitCurrentEntity({ accountId, entityId: entity._id }));
    } else {
      dispatch(setCurrentEntity(entity));
      dispatch(setModal({ show: true, type: MODAL_TYPES.SUBMIT_ENTITY_ERRORS }));
    }
  };

  const shareItem = entity => {
    dispatch(setCurrentEntity(entity));
    dispatch(setShareModal({ type: 'entities', id: entity._id, label: entity.name }));
    dispatch(setModal({ show: true, type: MODAL_TYPES.SHARE }));
  };

  const handleEntityClick = async entity => {
    dispatch(setCurrentEntity(entity));
    let authorizedSurveys = getAuthorizedSurveys();
    dispatch(setCurrentSurvey(authorizedSurveys?.[0]));
    history.push(paths.dashboardEntity);
  };

  const isValidEntityName = () => {
    let alreadyExists = activeAccount?.entities.some(entity => entity.name === newEntityName?.trim());
    return !!newEntityName && newEntityName?.trim().length !== 0 && !alreadyExists;
  };

  const getChipsLabel = entity => {
    let entityFillingPercentage = getEntityTotalFillingPercentage(entity);
    let { submitted, rejected, validated } = entity?.submission;
    if (submitted) {
      return { color: COLORS.Squash, label: 'En cours de vérification' };
    }
    if (rejected) {
      return { color: `#fab1a0`, label: 'Rejeté' };
    }
    if (validated) {
      return { color: COLORS.AquaMarine, label: 'Validé' };
    }
    if (entityFillingPercentage === 0) {
      return { color: COLORS.DarkGreen, label: 'À remplir' };
    }
    if (entityFillingPercentage > 0) {
      return { color: COLORS.lightNavy, label: 'En cours de mise en conformité' };
    }
  };

  useEffect(() => {
    const timeoutId = timerRef.current;
    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  return (
    <>
      <EntityTableContainer>
        <Top>
          <span>Mes entités</span>
          {currentUser?.role !== 'guest' && <Button onClick={handleShowNewEntityModal} icon={HiPlus} type={'light'} label={'ajouter une entité'} />}
        </Top>
        <RowsSlider>
          <RowsContainer>
            {activeAccount &&
              filterSharedEntities(activeAccount?.entities)?.map((entity, index) => {
                let isSubmitted = entity?.submission?.submitted === true;
                return (
                  <Row key={index} onClick={() => handleEntityClick(entity)}>
                    <Name>
                      {isSubmitted && <MdLockOutline />}
                      {entity.name}
                    </Name>
                    <Chips {...getChipsLabel(entity)} />
                    <Splitter />
                    <Data className={'completed-surveys'}>
                      <MdEditNote />
                      {totalSurveyCount(entity).completedSurveysCount === 0 ? (
                        <span>Accéder à mes questionnaires</span>
                      ) : (
                        <span>
                          {totalSurveyCount(entity).completedSurveysCount} / {totalSurveyCount(entity).totalSurveysCount} Questionnaires complétés
                        </span>
                      )}
                    </Data>
                    <PieContainer>
                      <Pie largePercent={true} inverted={true} color={COLORS.AquaMarine} percent={getEntityConformityPercentage(entity)} />
                    </PieContainer>
                    <MoreOptionsContainer>
                      <MoreOptions options={getEntityOptions(entity)} />
                    </MoreOptionsContainer>
                  </Row>
                );
              })}
          </RowsContainer>
        </RowsSlider>
      </EntityTableContainer>
      <Modal
        alignment={'center'}
        size={{ width: '50%' }}
        show={displayNewEntityModal}
        title={'Ajouter une nouvelle entité'}
        onClose={() => setDisplayNewEntityModal(false)}>
        <BasicModalContainer>
          <InputComponent
            focus={true}
            placeholder={"Nom de l'entité"}
            sharable={false}
            label={'Nom de l’entité à mettre en conformité'}
            type={'text'}
            onChange={e => setNewName(e.target.value)}
            error={activeAccount?.entities?.some(entity => entity.name === newEntityName?.trim()) && 'Ce nom est déjà utilisé'}
            value={newEntityName}
          />
          <ButtonContainer>
            <Button onClick={handleAddEntity} label={'Ajouter'} disabled={!isValidEntityName()} />
            <Button type={'light'} onClick={() => setDisplayNewEntityModal(false)} label={'Annuler'} />
          </ButtonContainer>
        </BasicModalContainer>
      </Modal>
      <Modal
        alignment={'center'}
        size={{ width: '50%' }}
        show={displayDeleteEntityModal?.show}
        title={`Êtes-vous sûr·e de vouloir supprimer <b>${displayDeleteEntityModal?.entity?.name}</b> ?`}
        onClose={closeDeleteEntityModal}>
        <BasicModalContainer>
          <p>
            Cette action est irréversible. Si vous supprimez l’entité, vous perdrez toutes les données liées à la mise en conformité de celle-ci.
            Voulez-vous continuer ?{' '}
          </p>
          <ButtonContainer>
            <Button type={'danger'} onClick={() => handleDeleteEntity(displayDeleteEntityModal?.entity)} label={'Supprimer'} />
            <Button type={'light'} onClick={closeDeleteEntityModal} label={'Annuler'} />
          </ButtonContainer>
        </BasicModalContainer>
      </Modal>
      <Modal
        alignment={'center'}
        size={{ width: '50%' }}
        show={displayRenameEntityModal?.show}
        title={`Quel nouveau nom souhaitez-vous donner à <b>${displayRenameEntityModal?.entity?.name}</b> ?`}
        onClose={closeRenameEntityModal}>
        <BasicModalContainer>
          <InputComponent
            focus={true}
            placeholder={'Nouveau nom ...'}
            sharable={false}
            label={'Nouveau nom de votre entité'}
            type={'text'}
            onChange={e => setNewName(e.target.value)}
            error={activeAccount?.entities.some(entity => entity.name === newEntityName?.trim()) && 'Ce nom est déjà utilisé'}
            value={newEntityName}
          />
          <ButtonContainer>
            <Button onClick={handleEntityChangeName} label={'Valider'} disabled={!isValidEntityName()} />
            <Button type={'light'} onClick={closeRenameEntityModal} label={'Annuler'} />
          </ButtonContainer>
        </BasicModalContainer>
      </Modal>
      <Modal
        alignment={'center'}
        size={{ width: '50%' }}
        show={displayDuplicateEntityModal?.show}
        title={`Quel nom souhaitez-vous donner à la copie de <b>${displayDuplicateEntityModal?.entity?.name}</b> ?`}
        onClose={closeDuplicateEntityModal}>
        <BasicModalContainer>
          <InputComponent
            focus={true}
            placeholder={'Nouveau nom ...'}
            sharable={false}
            label={'Nom de la copie'}
            type={'text'}
            onChange={e => setNewName(e.target.value)}
            error={activeAccount?.entities.some(entity => entity.name === newEntityName?.trim()) && 'Ce nom est déjà utilisé'}
            value={newEntityName}
          />
          <ButtonContainer>
            <Button onClick={handleCloneEntity} label={'Valider'} disabled={!isValidEntityName()} />
            <Button type={'light'} onClick={closeDuplicateEntityModal} label={'Annuler'} />
          </ButtonContainer>
        </BasicModalContainer>
      </Modal>
    </>
  );
};

const ButtonContainer = styled.div`
  display: flex;
  column-gap: 20px;
  align-items: center;
`;

const BasicModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  column-gap: 20px;
  align-items: center;
  row-gap: 26px;

  p {
    font-size: 1.3em;
    font-weight: 400;
    font-family: 'Roboto', serif;
    line-height: 1.5em;
  }
`;

const EntityTableContainer = styled.div`
  background-color: white;
  border: 1px solid rgba(0, 0, 0, 0.2);
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 10px;
`;

const Top = styled.div`
  border-bottom: 1px solid rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 60px;
  padding: 0 20px;
  user-select: none;

  > span {
    font-size: 26px;
    font-weight: 700;
    font-family: 'Nunito', serif;
  }
`;

const RowsSlider = styled.div`
  overflow-y: auto;
  flex: 1;
`;

const RowsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  display: flex;
  column-gap: 20px;
  height: 60px;
  padding: 0 25px;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(0, 0, 0, 0.2);
  cursor: pointer;
  background-color: white;
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
`;

const Name = styled.span`
  font-family: 'Roboto', serif;
  font-size: 16px;
  font-weight: 500;
  width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  column-gap: 5px;
  user-select: none;

  svg {
    color: ${COLORS.Squash};
    height: 24px;
    width: 24px;
  }
`;

const PieContainer = styled.div`
  height: calc(100% - 8px);
`;

const Splitter = styled.div`
  flex: 1;
`;

const Data = styled.div`
  display: flex;
  align-items: center;
  column-gap: 5px;
  font-size: 14px;
  font-family: 'Roboto', serif;
  font-weight: 400;
  width: 250px;
  position: relative;
  justify-content: center;

  span {
    white-space: nowrap;
  }

  &:before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 5px;
    top: -7px;
    left: 0;
    border: 2px solid rgba(0, 0, 0, 0);
    transition: border 0.2s ease-in-out;
    padding: 5px 0;
  }

  &:hover {
    cursor: pointer;

    &:before {
      border: 2px solid rgba(0, 0, 0, 0.4);
    }
  }

  svg {
    height: 24px;
    width: 24px;
    color: gray;
  }

  @media (max-width: 1260px) {
    &.collaborators,
    &.completed-surveys {
      display: none;
    }
  }
`;

const MoreOptionsContainer = styled.div`
  min-height: 38px;
  min-width: 38px;
`;
